import React from 'react';
import { UserX, FileQuestion, Skull, ShieldAlert } from 'lucide-react';
import { SectionHeader } from '../../components/SectionHeader.js';
import { Card } from '../../components/Card.js';

export const PainPoints: React.FC = () => {
  const painPoints = [
    {
      icon: <ShieldAlert className="w-7 h-7 text-rose-600" />,
      tag: '管理者ハッキングの脅威',
      title: '管理者アカウントが乗っ取られたら、バックアップごと消される',
      description:
        'Google標準のバックアップ（Google Vault）は、Google Workspaceと同じ管理画面の中にあります。もし管理者のID・パスワードが漏洩して乗っ取られた場合、バックアップや保持ルールごと一瞬ですべて消去されてしまう落とし穴があります。',
      impact: '管理画面乗っ取りによる全データ消失',
      tagColor: 'bg-rose-50 text-rose-700 border-rose-200',
    },
    {
      icon: <UserX className="w-7 h-7 text-amber-600" />,
      tag: '退職者アカウントの放置',
      title: '過去のメールやファイルを見るためだけに、毎月料金を払い続けていませんか？',
      description:
        '退職した社員のアカウントを削除すると、過去のメールやGoogleドライブのファイルが消えてしまいます。引き継ぎ確認やトラブル調査のためにアカウントを残したまま毎月数千円の料金を払い続け、会社全体で毎年数百万円が無駄になっています。',
      impact: '退職者100名で年間約300万円〜600万円の浪費',
      tagColor: 'bg-amber-50 text-amber-700 border-amber-200',
    },
    {
      icon: <FileQuestion className="w-7 h-7 text-indigo-600" />,
      tag: 'トラブル時の証拠能力',
      title: 'トラブル発生時、過去のメールやファイルが「本物」だと客観証明できますか？',
      description:
        '社内不正や情報漏洩、労務トラブルが起きたとき、提出したデータが「後から都合よく書き換えられたものではない」と客観的に証明するのは困難です。第三者に対して胸を張って「改ざんされていない原本」と証明できる確固たる証拠が必要です。',
      impact: '裁判や監査での決定的な証拠の欠落',
      tagColor: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    },
    {
      icon: <Skull className="w-7 h-7 text-red-600" />,
      tag: 'ランサムウェア感染',
      title: 'パソコンがウイルスに感染すると、Googleドライブのデータも巻き込まれて消える',
      description:
        '社員のパソコンがウイルス（ランサムウェア）に感染すると、Googleドライブのパソコン用アプリを通じて、クラウド上のファイルまで暗号化されて開けなくなります。復旧作業には多大な時間と費用がかかり、数週間の業務停止に追い込まれます。',
      impact: '業務長期停止による数億円規模の損失',
      tagColor: 'bg-red-50 text-red-700 border-red-200',
    },
  ];

  return (
    <section id="problem" className="py-20 md:py-28 relative border-t border-slate-200/80 bg-slate-100/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          tag="3つの重大な落とし穴"
          tagVariant="amber"
          title="Google Workspace 標準機能だけでは防げない「死角」"
          description="多くの企業が「Googleを使っているからバックアップも安心」と誤解しています。しかし、管理者のアカウント乗っ取り、退職者のデータ消去、毎月かさむライセンス費用は、Google Workspace 単体では解決できません。"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {painPoints.map((item, index) => (
            <Card key={index} className="flex flex-col justify-between bg-white border-slate-200/90 shadow-sm hover:shadow-md">
              <div>
                <div className="flex items-center justify-between mb-5">
                  <div className="p-3 rounded-2xl bg-slate-50 border border-slate-100">
                    {item.icon}
                  </div>
                  <span className={`text-xs font-mono px-3 py-1 rounded-full border font-semibold ${item.tagColor}`}>
                    {item.tag}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3 leading-snug">
                  {item.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-6">
                  {item.description}
                </p>
              </div>

              <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs font-medium">
                <span className="text-slate-500">想定される経営的脅威:</span>
                <span className="text-rose-600 font-bold">{item.impact}</span>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};
